import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Info, Plus, Search, Trash2, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  AddParticipantDialog,
  addBlockReason,
  buildParticipantRows,
  buildProfileNames,
  canRemoveParticipants,
  collectMemberIds,
  collectPlayerIds,
  compareParticipantRows,
  countOccupiedSlots,
  drawKindOf,
  filterParticipantRows,
  splitDrawnParticipantRows,
  splitParticipantRows,
  isPairCompetition,
  PARTICIPANT_STATUS_LABELS,
  PARTICIPANT_STATUS_TONE,
  useAddParticipantMutation,
  useCompetitionActionMutation,
  useCompetitionMatchesQuery,
  useCompetitionQuery,
  useParticipantsQuery,
  useRemoveParticipantMutation,
  useRemoveParticipantsMutation,
  useSetSeedMutation,
  type AddParticipantByOrganizerRequest,
  type ParticipantRow,
} from '@/features/competition';
import { getUsersByIds, userKeys } from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  IconButton,
  Input,
  Modal,
  PageHeader,
  ProgressRing,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  useToast,
} from '@/shared/ui';

/**
 * Ekran dodawania zawodników do rozgrywki — układ z makiety
 * `dodawanie_zawodnikow_do_turnieju.png`.
 *
 * Zgłoszenia liczone są slotami: w singlu slot to jedna osoba, w deblu i mikscie
 * para. Tak samo liczy je backend przy limicie miejsc i przy losowaniu.
 */
export function TournamentParticipantsPage() {
  const { t } = useTranslation('tournament');
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { clubId } = useSelectedClub();

  const competitionQuery = useCompetitionQuery(competitionId);
  const participantsQuery = useParticipantsQuery(competitionId);
  const addMutation = useAddParticipantMutation(competitionId, clubId);
  const removeMutation = useRemoveParticipantMutation(competitionId, clubId);
  const bulkRemoveMutation = useRemoveParticipantsMutation(competitionId, clubId);
  const seedMutation = useSetSeedMutation(competitionId, clubId);
  const actionMutation = useCompetitionActionMutation(clubId);

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<ParticipantRow | null>(null);
  // Zaznaczenie do masowego usuwania — klucz to userId zgloszenia (tym samym
  // identyfikatorem posluguje sie DELETE /participants/{userId}).
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const competition = competitionQuery.data;
  const participants = useMemo(() => participantsQuery.data ?? [], [participantsQuery.data]);

  // Nazwiska jednym batchem dla wszystkich osób ze zgłoszeń.
  const memberIds = useMemo(() => collectMemberIds(participants).sort(), [participants]);
  const profilesQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', memberIds.join(',')],
    queryFn: () => getUsersByIds(memberIds),
    enabled: memberIds.length > 0,
  });
  const names = useMemo(() => buildProfileNames(profilesQuery.data ?? []), [profilesQuery.data]);

  const rows = useMemo(
    () => buildParticipantRows(participants, names).sort(compareParticipantRows),
    [participants, names],
  );

  const occupied = countOccupiedSlots(participants);
  const maxPlayers = competition?.maxPlayers ?? 0;

  // Po losowaniu o składzie decydują mecze, nie kolejność zapisów. Zapytanie
  // dzieli klucz z `TournamentLayout`, więc zwykle idzie z pamięci podręcznej.
  const drawn =
    competition !== undefined &&
    (competition.status === 'IN_PROGRESS' || competition.status === 'COMPLETED');
  const matchesQuery = useCompetitionMatchesQuery(competitionId, drawn);
  const drawnMemberIds = useMemo(
    () => new Set(collectPlayerIds(matchesQuery.data ?? [])),
    [matchesQuery.data],
  );

  // Przed losowaniem listę dzieli kolejność zgłoszeń: pierwszych `maxPlayers`
  // gra, reszta czeka w rezerwie — dokładnie tak wybiera zawodników backend.
  // Po losowaniu rezerwy już nie ma (patrz `splitDrawnParticipantRows`).
  const sections = useMemo(
    () =>
      drawn
        ? splitDrawnParticipantRows(rows, drawnMemberIds)
        : splitParticipantRows(rows, maxPlayers),
    [drawn, rows, drawnMemberIds, maxPlayers],
  );
  const visible = useMemo(
    () => ({
      playing: filterParticipantRows(sections.playing, search),
      reserve: filterParticipantRows(sections.reserve, search),
      inactive: filterParticipantRows(sections.inactive, search),
    }),
    [sections, search],
  );
  const visibleCount = visible.playing.length + visible.reserve.length + visible.inactive.length;

  // Do masowego usuwania liczy sie tylko to, co widac — zaznaczenie przycinamy
  // do widocznych wierszy, zeby filtr wyszukiwania nie usunal kogos „w ciemno".
  const visibleRowList = useMemo(
    () => [...visible.playing, ...visible.reserve, ...visible.inactive],
    [visible],
  );
  const selectedIds = useMemo(
    () =>
      visibleRowList
        .map((row) => row.participant.userId)
        .filter((userId) => selected.has(userId)),
    [visibleRowList, selected],
  );
  const allVisibleSelected =
    visibleRowList.length > 0 && selectedIds.length === visibleRowList.length;
  // Po losowaniu skład znamy dopiero z meczów — do tego czasu nie pokazujemy
  // ani tabeli, ani pustego stanu, żeby lista nie mrugnęła „brak zawodników”.
  const rosterPending = participantsQuery.isPending || (drawn && matchesQuery.isPending);

  const blockReason = addBlockReason(competition);
  const canRemove = canRemoveParticipants(competition);
  // Kolumna checkboxow istnieje tylko, gdy usuwanie jest mozliwe.
  const columnCount = canRemove ? 6 : 5;
  const pair = competition ? isPairCompetition(competition.matchType) : false;

  /** Komunikat błędu z API albo tekst zastępczy. */
  function describe(error: unknown, fallback: string): string {
    return error instanceof ApiError ? error.message : fallback;
  }

  function handleAdd(request: AddParticipantByOrganizerRequest) {
    setAddError(null);
    addMutation.mutate(request, {
      onSuccess: () => {
        setDialogOpen(false);
        showToast({ tone: 'success', description: t('participants.toast.added') });
      },
      onError: (error) => setAddError(describe(error, t('participants.toast.addFailed'))),
    });
  }

  function handleRemove(row: ParticipantRow) {
    removeMutation.mutate(row.participant.userId, {
      onSuccess: () => {
        setPendingRemove(null);
        showToast({ tone: 'success', description: t('participants.toast.removed') });
      },
      onError: (error) => {
        setPendingRemove(null);
        showToast({ tone: 'danger', description: describe(error, t('participants.toast.removeFailed')) });
      },
    });
  }

  /** Zaznacza albo odznacza jedno zgłoszenie. */
  function toggleSelected(userId: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  }

  /** Zaznacza wszystkie widoczne zgłoszenia albo czyści zaznaczenie. */
  function toggleAllVisible(checked: boolean) {
    setSelected(
      checked ? new Set(visibleRowList.map((row) => row.participant.userId)) : new Set(),
    );
  }

  function handleBulkRemove() {
    bulkRemoveMutation.mutate(selectedIds, {
      onSuccess: ({ removed, failed }) => {
        setBulkConfirmOpen(false);
        setSelected(new Set());
        if (failed === 0) {
          showToast({
            tone: 'success',
            description: t('participants.toast.bulkRemoved', { count: removed }),
          });
        } else {
          showToast({
            tone: 'danger',
            description: t('participants.toast.bulkRemoveFailed', { removed, failed }),
          });
        }
      },
    });
  }

  /** Zapisuje rozstawienie; pusta wartość je zdejmuje. */
  function handleSeed(row: ParticipantRow, raw: string) {
    const trimmed = raw.trim();
    const seed = trimmed ? Number(trimmed) : null;
    if (seed !== null && (!Number.isInteger(seed) || seed < 1)) return;
    if (seed === row.participant.seed) return;

    seedMutation.mutate(
      { userId: row.participant.userId, seed },
      {
        onError: (error) =>
          showToast({ tone: 'danger', description: describe(error, t('participants.toast.seedFailed')) }),
      },
    );
  }

  function runLifecycle(
    action: 'open-registration' | 'close-registration',
    message: string,
    thenGoTo?: string,
  ) {
    actionMutation.mutate(
      { competitionId, action },
      {
        onSuccess: () => {
          void competitionQuery.refetch();
          showToast({ tone: 'success', description: message });
          // Nawigujemy dopiero po udanej odpowiedzi — inaczej przy 409 uzytkownik
          // wyladowalby na kolejnym ekranie z komunikatem o bledzie.
          if (thenGoTo) void navigate(thenGoTo);
        },
        onError: (error) =>
          showToast({ tone: 'danger', description: describe(error, t('participants.toast.actionFailed')) }),
      },
    );
  }

  /** Wiersz zgłoszenia; rezerwowi są przygaszeni — czekają na zwolnienie miejsca. */
  function renderRow(row: ParticipantRow, ordinal: number, reserve = false) {
    return (
      <TableRow key={row.participant.id} className={reserve ? 'opacity-70' : undefined}>
        {canRemove && (
          <TableCell>
            <Checkbox
              aria-label={t('participants.selectOne', { name: row.label })}
              checked={selected.has(row.participant.userId)}
              onChange={(event) => toggleSelected(row.participant.userId, event.target.checked)}
            />
          </TableCell>
        )}
        <TableCell className="text-fg-subtle text-sm tabular-nums">{ordinal}</TableCell>

        <TableCell>
          <div className="flex items-center gap-2.5">
            <Avatar name={row.members[0]?.name ?? '?'} size="sm" colorful />
            <div className="min-w-0">
              <p className="text-fg-strong truncate text-sm font-semibold">{row.label}</p>
              {row.participant.groupNumber !== null && (
                <p className="text-fg-muted text-xs">
                  {t('participants.group', { number: row.participant.groupNumber })}
                </p>
              )}
            </div>
          </div>
        </TableCell>

        <TableCell>
          <Input
            inputSize="sm"
            type="number"
            min={1}
            aria-label={t('participants.seedFor', { name: row.label })}
            defaultValue={row.participant.seed ?? ''}
            disabled={!canRemove}
            onBlur={(event) => handleSeed(row, event.target.value)}
            className="w-16"
          />
        </TableCell>

        <TableCell>
          {reserve ? (
            <Badge tone="warning" size="sm">
              {t('participants.reserveBadge')}
            </Badge>
          ) : (
            <Badge tone={PARTICIPANT_STATUS_TONE[row.participant.status]} size="sm">
              {PARTICIPANT_STATUS_LABELS[row.participant.status]}
            </Badge>
          )}
        </TableCell>

        <TableCell>
          <div className="flex justify-end">
            <IconButton
              variant="danger"
              size="sm"
              icon={<Trash2 />}
              label={t('participants.remove')}
              disabled={!canRemove}
              onClick={() => setPendingRemove(row)}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  }

  if (competitionQuery.isError) {
    return (
      <>
        <PageHeader title={t('participants.title')} />
        <Alert tone="danger">{t('participants.loadError')}</Alert>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Nagłówek turnieju i zakładki daje TournamentLayout. */}
      <PageHeader
        title={t('participants.title')}
        description={
          competition ? t('participants.description', { name: competition.name }) : undefined
        }
      />

      {/* Zapisy trzeba otworzyć, zanim backend przyjmie pierwsze zgłoszenie. */}
      {competition?.status === 'DRAFT' && (
        <Alert tone="info">
          <span className="flex flex-wrap items-center gap-3">
            {t('participants.draftHint')}
            <Button
              size="sm"
              loading={actionMutation.isPending}
              onClick={() => runLifecycle('open-registration', t('participants.toast.opened'))}
            >
              {t('participants.openRegistration')}
            </Button>
          </span>
        </Alert>
      )}

      {/* Limit nie blokuje zapisów — nadmiarowe zgłoszenia idą na listę rezerwową. */}
      {competition?.status === 'REGISTRATION_OPEN' && occupied >= maxPlayers && maxPlayers > 0 && (
        <Alert tone="info">{t('participants.reserveInfo')}</Alert>
      )}

      {competition &&
        competition.status !== 'DRAFT' &&
        competition.status !== 'REGISTRATION_OPEN' && (
          <Alert tone="warning">{t('participants.closed')}</Alert>
        )}

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              leadingIcon={<Plus aria-hidden="true" />}
              disabled={blockReason !== null}
              onClick={() => {
                setAddError(null);
                setDialogOpen(true);
              }}
            >
              {pair ? t('participants.addPair') : t('participants.add')}
            </Button>

            <Input
              inputSize="sm"
              type="search"
              aria-label={t('participants.search')}
              placeholder={t('participants.search')}
              leadingIcon={<Search />}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-56"
            />

            {canRemove && selectedIds.length > 0 && (
              <Button
                variant="danger"
                leadingIcon={<Trash2 aria-hidden="true" />}
                onClick={() => setBulkConfirmOpen(true)}
              >
                {t('participants.removeSelected', { count: selectedIds.length })}
              </Button>
            )}
          </div>

          {rosterPending && <Skeleton className="h-64 w-full" />}

          {!rosterPending && participantsQuery.isSuccess && rows.length === 0 && (
            <EmptyState
              icon={<Users />}
              title={t('participants.emptyTitle')}
              description={t('participants.emptyDescription')}
              className="min-h-[16rem]"
            />
          )}

          {visibleCount > 0 && (
            <TableContainer>
              <Table className="[&_td]:px-3 [&_th]:px-3">
                <TableHead>
                  <TableRow>
                    {canRemove && (
                      <TableHeaderCell className="w-10">
                        <Checkbox
                          aria-label={t('participants.selectAll')}
                          checked={allVisibleSelected}
                          onChange={(event) => toggleAllVisible(event.target.checked)}
                        />
                      </TableHeaderCell>
                    )}
                    <TableHeaderCell className="w-12">
                      {t('participants.columns.index')}
                    </TableHeaderCell>
                    <TableHeaderCell>{t('participants.columns.player')}</TableHeaderCell>
                    <TableHeaderCell className="w-24">
                      {t('participants.columns.seed')}
                    </TableHeaderCell>
                    <TableHeaderCell className="w-32">
                      {t('participants.columns.status')}
                    </TableHeaderCell>
                    <TableHeaderCell className="w-20 text-right">
                      {t('participants.columns.actions')}
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visible.playing.map((row, index) => renderRow(row, index + 1))}

                  {/* Kreska oddzielająca zgłoszenia ponad limit miejsc */}
                  {visible.reserve.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={columnCount} className="bg-surface-sunken py-2">
                        <span className="text-fg-muted flex items-center gap-3 text-[0.6875rem] font-bold tracking-[0.08em] uppercase">
                          <span aria-hidden="true" className="border-border-strong flex-1 border-t" />
                          {t('participants.reserveDivider', { count: sections.reserve.length })}
                          <span aria-hidden="true" className="border-border-strong flex-1 border-t" />
                        </span>
                      </TableCell>
                    </TableRow>
                  )}
                  {visible.reserve.map((row, index) =>
                    renderRow(row, visible.playing.length + index + 1, true),
                  )}

                  {/* Wycofani i zablokowani — nie zajmują miejsc */}
                  {visible.inactive.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={columnCount} className="bg-surface-sunken py-2">
                        <span className="text-fg-subtle flex items-center gap-3 text-[0.6875rem] font-bold tracking-[0.08em] uppercase">
                          <span aria-hidden="true" className="border-border flex-1 border-t" />
                          {t('participants.inactiveDivider')}
                          <span aria-hidden="true" className="border-border flex-1 border-t" />
                        </span>
                      </TableCell>
                    </TableRow>
                  )}
                  {visible.inactive.map((row, index) =>
                    renderRow(row, visible.playing.length + visible.reserve.length + index + 1),
                  )}
                </TableBody>
              </Table>

              <TableFooter>
                {t('participants.footer', { shown: visibleCount, total: rows.length })}
              </TableFooter>
            </TableContainer>
          )}
        </div>

        {/* Kolumna prawa — licznik miejsc i domknięcie listy */}
        <div className="flex flex-col gap-4 xl:sticky xl:top-6">
          <Card padding="lg" className="flex flex-col items-center gap-3">
            <CardHeader title={t('participants.panel.title')} className="mb-0 self-stretch" />
            <ProgressRing
              // Po losowaniu licznik pokazuje realny skład — `occupied` liczy
              // też zgłoszenia ponad limit, których losowanie nie wzięło.
              value={drawn ? sections.playing.length : Math.min(occupied, maxPlayers)}
              max={maxPlayers}
              caption={pair ? t('participants.panel.pairs') : t('participants.panel.players')}
            />
            <p className="text-fg-muted text-sm">
              {t('participants.panel.limit', { max: maxPlayers })}
            </p>
            {sections.reserve.length > 0 && (
              <p className="text-warning-600 text-sm font-semibold">
                {t('participants.panel.reserve', { count: sections.reserve.length })}
              </p>
            )}
            <p className="text-fg-subtle text-xs">{t('participants.panel.minimum')}</p>
          </Card>

          <Alert tone="info">
            <span className="flex gap-2">
              <Info className="mt-0.5 size-4 shrink-0" />
              {t('participants.tip')}
            </span>
          </Alert>

          {competition?.status === 'REGISTRATION_OPEN' && (
            <Button
              size="lg"
              fullWidth
              trailingIcon={<ArrowRight className="size-4" />}
              loading={actionMutation.isPending}
              onClick={() =>
                runLifecycle(
                  'close-registration',
                  t('participants.toast.closed'),
                  // Zamknięcie zapisów prowadzi wprost do losowania; systemy bez
                  // losowania (Challenge) wracają na listę turniejów.
                  drawKindOf(competition.system) !== 'none'
                    ? `/turnieje/${competitionId}/losowanie`
                    : '/turnieje',
                )
              }
            >
              {t('participants.closeList')}
            </Button>
          )}

          {competition?.status === 'REGISTRATION_CLOSED' &&
            drawKindOf(competition.system) !== 'none' && (
              <Button
                size="lg"
                fullWidth
                trailingIcon={<ArrowRight className="size-4" />}
                onClick={() => void navigate(`/turnieje/${competitionId}/losowanie`)}
              >
                {t('participants.goToDraw')}
              </Button>
            )}
        </div>
      </div>

      {competition && dialogOpen && (
        <AddParticipantDialog
          onClose={() => setDialogOpen(false)}
          matchType={competition.matchType}
          onSubmit={handleAdd}
          submitting={addMutation.isPending}
          error={addError}
        />
      )}

      <Modal
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        size="sm"
        title={t('participants.confirmRemove.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingRemove(null)}>
              {t('participants.confirmRemove.keep')}
            </Button>
            <Button
              variant="danger"
              loading={removeMutation.isPending}
              onClick={() => pendingRemove && handleRemove(pendingRemove)}
            >
              {t('participants.confirmRemove.confirm')}
            </Button>
          </>
        }
      >
        {pendingRemove && t('participants.confirmRemove.body', { name: pendingRemove.label })}
      </Modal>

      {/* Potwierdzenie masowego usuwania zaznaczonych zgłoszeń. */}
      <Modal
        open={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        size="sm"
        title={t('participants.confirmBulkRemove.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBulkConfirmOpen(false)}>
              {t('participants.confirmBulkRemove.keep')}
            </Button>
            <Button
              variant="danger"
              loading={bulkRemoveMutation.isPending}
              onClick={handleBulkRemove}
            >
              {t('participants.confirmBulkRemove.confirm')}
            </Button>
          </>
        }
      >
        {t('participants.confirmBulkRemove.body', { count: selectedIds.length })}
      </Modal>
    </div>
  );
}
